import { dialogs, extensions, integration, view } from '@yola/ws-sdk';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import mediaAttributes from 'src/js/modules/extensions/common/constants/media-attributes';
import store from 'src/js/store';
import constants from '../constants/common';
import config from '../constants/config';
import imageFileFormats from '../constants/image-file-formats';
import cacheSiteImage from './cache-site-image';
import getImageUrlFromPasteEvent from './get-image-url-from-paste-event';
import isPlaceholder from './is-placeholder';
import matchesOrContains from './matches-or-contains';
import pasteImageFromClipboard from './paste-image-from-clipboard';

const handlePasteEvent = async (event) => {
  const settings = extensions.accessors.getExtensionSettings(config.slug);
  const state = store.getState();
  const focusedElementId = view.selectors.getFocusedElement(state);
  const featureFlags = await integration.helpers.requestFeatureFlags(['image_copy_paste']);

  const { image_copy_paste: isCopyPasteEnabled } = featureFlags;

  if (!isCopyPasteEnabled || focusedElementId) return;

  const selectedElementId = view.selectors.getSelectedElement(state);
  const hoveredElementId = view.selectors.getHoveredElement(state);
  const targetElementId = selectedElementId || hoveredElementId;
  const element = view.accessors.getLiveElement(targetElementId);

  if (
    !targetElementId ||
    !element?.matches(`${settings.imageContainer}, ${settings.blockWithBackgroundQuerySelector}`)
  ) {
    return;
  }

  let urlData;
  try {
    urlData = await getImageUrlFromPasteEvent(event);
  } catch (e) {
    if (e.message === constants.WRONG_FILE_TYPE_ERROR) {
      dialogs.operations.show(dialogTypes.FILE_TYPE_ERROR_DIALOG, {
        formats: imageFileFormats.map((item) => `.${item}`).join(', '),
      });
    } else {
      console.error(e);
      dialogs.operations.hide();
    }
    return;
  }

  if (urlData) {
    const { source: url, altSource: altUrl } = urlData;
    const isBackgroundImage = element.matches(settings.blockWithBackgroundQuerySelector);
    const isGalleryImage = element.matches(settings.galleryItemSelector);
    const imageElement = matchesOrContains(element, settings.imageActionSelector);
    const imageElementId = view.accessors.getLiveElementId(imageElement);
    const cachedImg = await cacheSiteImage(url);

    let actions = [];

    if (isBackgroundImage) {
      const backgroundUrl = altUrl ? `${altUrl}, ${url}` : url;
      actions = [
        view.actions.setElementAttribute(targetElementId, 'background-image', backgroundUrl),
        view.actions.setElementAttribute(targetElementId, 'background-position', '50% 50%'),
        view.actions.setElementAttribute(targetElementId, 'background-scale', '1'),
        view.actions.removeElementAttribute(targetElementId, 'background-placeholder'),
      ];
    } else {
      if (isGalleryImage) {
        actions = [
          view.actions.setElementAttribute(targetElementId, 'origin-src', url),
          view.actions.setElementAttribute(targetElementId, 'origin-width', cachedImg.naturalWidth),
          view.actions.setElementAttribute(
            targetElementId,
            'origin-height',
            cachedImg.naturalHeight
          ),
        ];
      }

      actions.push(
        view.actions.setElementAttribute(imageElementId, 'src', url),
        view.actions.setElementAttribute(targetElementId, 'content-position', '50% 50%'),
        view.actions.setElementAttribute(targetElementId, 'content-scale', '1')
      );
    }

    const isImagePlaceholder = isPlaceholder(element);
    if (isImagePlaceholder) {
      const attrToRemove = isBackgroundImage
        ? mediaAttributes.BACKGROUND_PLACEHOLDER_ATTRIBUTE
        : mediaAttributes.PLACEHOLDER_ATTRIBUTE;
      actions.push(view.actions.removeElementAttribute(targetElementId, attrToRemove));
    }

    store.dispatch(view.actions.bulkViewActions(actions));
    dialogs.operations.hide();
  } else {
    dialogs.operations.hide();
    await pasteImageFromClipboard(element, config);
  }
};

export default handlePasteEvent;
